import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useGetPlansQuery,
  useDeletePlanMutation,
} from "../../services/subscription";
import { useTheme } from "../../contexts/ThemeContext";
import { Plan } from "../../types/subscription/subscriptionScreen/subscriptionScreen.interface";
import type { RootState } from "../../redux/store";
import { useAppSelector } from "../../redux/hooks";
import { adminRoutes } from "../../routers/AdminRouters";

const PlanList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [deletePlan, { isLoading: isDeleting }] = useDeletePlanMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("");
  const [page, setPage] = useState(1);
  const [allPlans, setAllPlans] = useState<Plan[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastPlanRef = useRef<HTMLTableRowElement>(null);

  const limit = 10;

  const { data, isLoading, isError, isFetching } = useGetPlansQuery({
    name: searchTerm,
    type: filterType,
    page,
    limit,
  });

  // Handle uncertain API response structure
  const plans: Plan[] = data?.results || data?.data?.results || [];
  const totalCount = data?.totalCount || 0;

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPage(1);
    setAllPlans([]);
    setHasMore(true);
  }, [searchTerm, filterType]);

  // Append new plans to existing list
  useEffect(() => {
    if (plans.length > 0) {
      if (page === 1) {
        setAllPlans(plans);
      } else {
        setAllPlans((prev) => {
          const existingIds = new Set(prev.map((p) => p._id));
          const newPlans = plans.filter((p) => !existingIds.has(p._id));
          return [...prev, ...newPlans];
        });
      }
      // Check if there are more plans to load
      setHasMore(allPlans.length + plans.length < totalCount);
    } else if (page === 1) {
      setAllPlans([]);
      setHasMore(false);
    }
  }, [plans, page]);

  // Infinite scroll observer
  const lastPlanElementRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (isLoading || isFetching) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoading, isFetching, hasMore],
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    planId: string | null;
    planName: string;
  }>({ show: false, planId: null, planName: "" });

  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const cardClass =
    theme === "dark"
      ? "bg-[#1E1E1E] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const thClass =
    theme === "dark"
      ? "px-4 py-3 text-left text-sm font-bold text-[#F3F3F6] border-b border-[#2C2C2C]"
      : "px-4 py-3 text-left text-sm font-bold text-gray-700 border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-4 py-3 text-sm text-text-table border-b border-[#2C2C2C]"
      : "px-4 py-3 text-sm text-text-table border-b border-gray-200";

  const handleDeleteClick = (planId: string, planName: string) => {
    setDeleteConfirm({ show: true, planId, planName });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.planId) {
      try {
        await deletePlan(deleteConfirm.planId).unwrap();
        setDeleteConfirm({ show: false, planId: null, planName: "" });
      } catch (error) {
        console.error("Failed to delete plan:", error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ show: false, planId: null, planName: "" });
  };

  // Sidebar width handling
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">Plan Overview</h1>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <Link
              to="/"
              className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
            >
              Dashboard
            </Link>
            <span
              className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
            >
              /
            </span>
            <span className="text-[#B6CB24] font-medium">
              All Plans Details
            </span>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden`}>
        <div
          className={
            theme === "dark"
              ? "px-4 py-3 border-b border-[#2C2C2C] text-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-4"
              : "px-4 py-3 border-b border-gray-200 text-gray-800 flex flex-col md:flex-row md:items-center justify-between gap-4"
          }
        >
          <span className="text-lg font-semibold">All Plans Details</span>

          <div className="flex items-center gap-3 flex-1 justify-end">
            {/* Search */}
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${theme === "dark"
                ? "bg-[#0B0B0B] border-[#2C2C2C]"
                : "bg-gray-50 border-gray-200"
                } w-full md:w-64`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M17.5 17.5L22 22"
                  stroke={theme === "dark" ? "#ADADAD" : "#6B7280"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="7.5"
                  stroke={theme === "dark" ? "#ADADAD" : "#6B7280"}
                  strokeWidth="1.5"
                />
              </svg>
              <input
                type="text"
                placeholder="Search in table"
                className={`bg-transparent border-none outline-none text-sm w-full ${theme === "dark" ? "text-gray-200" : "text-gray-800"
                  } placeholder-gray-500`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border ${theme === "dark"
                ? "bg-[#0B0B0B] border-[#2C2C2C] text-gray-400"
                : "bg-gray-50 border-gray-200 text-gray-600"
                } w-32 cursor-pointer text-sm`}
            >
              <option value="">All Types</option>
              <option value="BASIC">BASIC</option>
              <option value="PREMIUM">PREMIUM</option>
            </select>

            {/* Create Button */}
            <Link
              to={adminRoutes.planCreate}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] hover:bg-[#B6CB24]/10 text-sm font-medium whitespace-nowrap"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8.00012V16.0001M16 12.0001H8"
                  stroke="#C6D92C"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Create New Plan
            </Link>
          </div>
        </div>

        <div
          className="overflow-x-auto"
          style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
        >
          <table className="min-w-full">
            <thead
              className="sticky top-0 z-10"
              style={{
                backgroundColor: theme === "dark" ? "#1E1E1E" : "#ffffff",
              }}
            >
              <tr className="bg-gray-610 text-gray-700">
                <th className={thClass}>License Name</th>
                <th className={thClass}>Tier</th>
                <th className={thClass}>Segment</th>
                <th className={thClass}>No. Screens</th>
                <th className={thClass}>Plan Type</th>
                <th className={thClass}>Action</th>
              </tr>
            </thead>
            <tbody ref={tableBodyRef}>
              {isLoading && page === 1 && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    Loading...
                  </td>
                </tr>
              )}
              {isError && !isLoading && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    Failed to load plans.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !isError &&
                allPlans.map((plan: Plan, index: number) => {
                  const isLastPlan = index === allPlans.length - 1;

                  return (
                    <tr
                      key={plan._id}
                      ref={isLastPlan ? lastPlanElementRef : null}
                    >
                      <td className={tdClass}>
                        {plan.name}{" "}
                        <span className="text-xs opacity-60">
                          ({(plan.type || "").charAt(0).toUpperCase() + (plan.type || "").slice(1).toLowerCase()}{" "}
                          {(plan.cycle || "").charAt(0).toUpperCase() + (plan.cycle || "").slice(1).toLowerCase()}{" "}
                          Plan)
                        </span>
                      </td>
                      <td className={tdClass}>
                        {plan.segment?.name || "--"}
                      </td>{" "}
                      {/* Tier Placeholder */}
                      <td className={tdClass}>
                        {plan.tier?.name || "--"}
                      </td>{" "}
                      {/* Segment Placeholder */}
                      <td className={tdClass}>{plan.screens || "-"}</td>{" "}
                      {/* No. Screens Placeholder */}
                      <td className={tdClass}>{plan.type}</td>
                      <td className={tdClass}>
                        <div className="flex items-center gap-3">
                          {/* EDIT */}
                          <button
                            title="Edit"
                            className="p-1 rounded hover:bg-white/5"
                            onClick={() => navigate(`/plan/${plan._id}`)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                                stroke="#F3F3F6"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>

                          {/* DELETE */}
                          <button
                            title="Delete"
                            className="p-1 rounded hover:bg-white/5"
                            onClick={() =>
                              handleDeleteClick(plan._id, plan.name)
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                                fill="url(#paint0_linear_278_6482)"
                              />
                              <path
                                d="M9.5 16.5V10.5"
                                stroke="url(#paint1_linear_278_6482)"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                              <path
                                d="M14.5 16.5V10.5"
                                stroke="url(#paint2_linear_278_6482)"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                              <defs>
                                <linearGradient
                                  id="paint0_linear_278_6482"
                                  x1="12"
                                  y1="1.25"
                                  x2="12"
                                  y2="22.75"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#D92626" />
                                  <stop offset="1" stopColor="#AD1F1F" />
                                </linearGradient>
                                <linearGradient
                                  id="paint1_linear_278_6482"
                                  x1="10"
                                  y1="10.5"
                                  x2="10"
                                  y2="16.5"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#D92626" />
                                  <stop offset="1" stopColor="#AD1F1F" />
                                </linearGradient>
                                <linearGradient
                                  id="paint2_linear_278_6482"
                                  x1="15"
                                  y1="10.5"
                                  x2="15"
                                  y2="16.5"
                                  gradientUnits="userSpaceOnUse"
                                >
                                  <stop stopColor="#D92626" />
                                  <stop offset="1" stopColor="#AD1F1F" />
                                </linearGradient>
                              </defs>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

              {!isLoading && !isError && allPlans.length === 0 && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    No plans found.
                  </td>
                </tr>
              )}
              {isFetching && page > 1 && (
                <tr>
                  <td className={`${tdClass} text-center`} colSpan={8}>
                    Loading more...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
            }`}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
              ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
              : "bg-white text-gray-900 border-gray-200"
              }`}
          >
            <button
              onClick={handleCancelDelete}
              aria-label="Close"
              className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
                }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>

            <div className="px-6 py-6 text-center">
              <div
                className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19.5 5.5L19.0982 12.0062M4.5 5.5L5.10461 15.5248C5.25945 18.0922 5.33688 19.3759 5.97868 20.299C6.296 20.7554 6.7048 21.1407 7.17905 21.4302C7.85035 21.84 8.68108 21.9631 10 22" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M20 15L13 21.9995M20 22L13 15.0005" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete Plan</h3>
              <p
                className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-6`}
              >
                Are you sure you want to delete "{deleteConfirm.planName}"?
              </p>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                  style={{
                    border: "1px solid #D92C2C",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Confirm and delete"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border"
                  style={{
                    border: "1px solid var(--menu-active-border, #C6D92C)",
                    background:
                      "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  }}
                  disabled={isDeleting}
                >
                  May be later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanList;
